import { getSystemCategory } from './systemTypeConstants';

export type EstadoCobertura =
  | 'sin_iniciar'
  | 'al_dia'
  | 'proximo'
  | 'vencido'
  | 'omitido'
  | 'requiere_aclaracion'; // PCI: trimestral registrada sin saber si era 1T/2T/3T

export interface ChecklistInput {
  fecha: string; // ISO (fecha de ejecución, fallback)
  periodo?: string | null; // 'YYYY-MM' introducido manualmente — PRIORITARIO sobre fecha
  tipo_revision: string; // 'trimestral' | 'anual' | otros (ignorados)
  estado: string; // 'completado' | 'finalizada' | 'en_progreso'
  numero_trimestral?: number | null; // PCI: 1, 2 ó 3 — sólo aplica a trimestrales
}

export type TipoProxima = 'anual' | 'trimestral_1' | 'trimestral_2' | 'trimestral_3';

export const tipoProximaLabel = (t: TipoProxima): string => {
  switch (t) {
    case 'anual': return 'Anual';
    case 'trimestral_1': return '1ª Trimestral (1T)';
    case 'trimestral_2': return '2ª Trimestral (2T)';
    case 'trimestral_3': return '3ª Trimestral (3T)';
  }
};

export interface CoberturaResult {
  estado: EstadoCobertura;
  ultima?: { fecha: Date; tipo: 'trimestral' | 'anual'; numeroTrimestral?: number | null };
  proxima?: { fecha: Date; tipo: TipoProxima };
  proximaAnual?: { fecha: Date }; // Siempre que sea calculable, fecha de la próxima anual
  diasDesfase: number; // negativo = faltan días, positivo = días vencida
  categoria: 'seguridad' | 'pci';
}

const DIAS_AVISO = 30;

/** Convierte periodo 'YYYY-MM' a Date (día 1 del mes). Null si inválido. */
function periodoToDate(periodo?: string | null): Date | null {
  if (!periodo) return null;
  const m = /^(\d{4})-(\d{2})$/.exec(periodo);
  if (!m) return null;
  return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, 1);
}

function addMonths(date: Date, months: number): Date {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** Periodo de próxima revisión fijado manualmente cuando no hay historial. */
export interface AnclaRevision {
  periodo: string; // 'YYYY-MM'
  tipo: TipoProxima;
}

const OFFSET_TIPO: Record<TipoProxima, number> = {
  anual: 0,
  trimestral_1: 3,
  trimestral_2: 6,
  trimestral_3: 9,
};

export function calcularCobertura(
  sistemaTipo: string,
  checklists: ChecklistInput[],
  omitida: boolean,
  hoy: Date = new Date(),
  ancla?: AnclaRevision | null,
): CoberturaResult {
  const categoria = getSystemCategory(sistemaTipo);

  if (omitida) {
    return { estado: 'omitido', diasDesfase: 0, categoria };
  }

  const anclaFecha = ancla ? periodoToDate(ancla.periodo) : null;

  // Sólo finalizados/completados y de tipos relevantes (trimestral/anual), ordenados asc.
  // La fecha que cuenta es la del periodo introducido manualmente (MM-AAAA); si no hay,
  // se usa la fecha de ejecución como fallback.
  const relevantes = checklists
    .filter(c => c.estado === 'completado' || c.estado === 'finalizada')
    .filter(c => c.tipo_revision === 'trimestral' || c.tipo_revision === 'anual')
    .map(c => ({
      fecha: periodoToDate(c.periodo) ?? new Date(c.fecha),
      tipo: c.tipo_revision as 'trimestral' | 'anual',
      numeroTrimestral: c.numero_trimestral ?? null,
    }))
    .sort((a, b) => a.fecha.getTime() - b.fecha.getTime());

  if (relevantes.length === 0 && !anclaFecha) {
    return { estado: 'sin_iniciar', diasDesfase: 0, categoria };
  }

  const ultima = relevantes.length > 0 ? relevantes[relevantes.length - 1] : null;
  const ultimaInfo = ultima
    ? { fecha: ultima.fecha, tipo: ultima.tipo, numeroTrimestral: ultima.numeroTrimestral }
    : undefined;

  if (categoria === 'seguridad') {
    // Sistemas de seguridad: sólo anual, cada 12 meses desde la última anual.
    // Sin historial, la próxima es la fecha fijada manualmente.
    const ultimaAnual = ultima
      ? ([...relevantes].reverse().find(c => c.tipo === 'anual') ?? ultima)
      : null;
    const proximaFecha = ultimaAnual ? addMonths(ultimaAnual.fecha, 12) : anclaFecha!;
    const hoy0 = startOfDay(hoy);
    const diasDesfase = Math.round((hoy0.getTime() - startOfDay(proximaFecha).getTime()) / 86400000);
    const estado: EstadoCobertura =
      diasDesfase > 0 ? 'vencido' : diasDesfase >= -DIAS_AVISO ? 'proximo' : 'al_dia';
    return {
      estado,
      ultima: ultimaInfo,
      proxima: { fecha: proximaFecha, tipo: 'anual' },
      proximaAnual: { fecha: proximaFecha },
      diasDesfase,
      categoria,
    };
  }

  // ---------- PCI ----------
  // Calendario ESTRICTO anclado a la PRIMERA anual registrada (o al periodo fijado a mano).
  // Secuencia: A (ancla) → 1T (+3m) → 2T (+6m) → 3T (+9m) → A (+12m) → 1T (+15m) ...
  // La fecha real de ejecución NO desplaza el calendario; sólo cubre el hueco teórico.

  const primeraAnual = relevantes.find(c => c.tipo === 'anual');

  if (!primeraAnual && !anclaFecha) {
    // Sin ancla anual: el ciclo PCI no ha empezado. Trimestrales sueltas no cuentan.
    return {
      estado: 'sin_iniciar',
      ultima: ultimaInfo,
      diasDesfase: 0,
      categoria,
    };
  }

  // Con periodo fijado manualmente, el ciclo empieza en la revisión indicada:
  // se deriva la anual equivalente y se ignoran los huecos anteriores.
  const anclaAnual = primeraAnual
    ? primeraAnual.fecha
    : addMonths(anclaFecha!, -OFFSET_TIPO[ancla!.tipo]);
  const slotMinimo = primeraAnual ? null : anclaFecha!;


  // Generar slots teóricos desde el ancla hasta hoy + 12 meses (margen).
  type Slot = { tipo: TipoProxima; fecha: Date; n: number };
  const slots: Slot[] = [];
  const hoy0 = startOfDay(hoy);
  const limite = addMonths(hoy0, 12);
  for (let n = 0; ; n++) {
    const offsetMeses = n * 3;
    const fecha = addMonths(anclaAnual, offsetMeses);
    const posCiclo = n % 4; // 0=anual, 1=1T, 2=2T, 3=3T
    const tipo: TipoProxima =
      posCiclo === 0 ? 'anual' :
      posCiclo === 1 ? 'trimestral_1' :
      posCiclo === 2 ? 'trimestral_2' : 'trimestral_3';
    slots.push({ tipo, fecha, n });
    if (fecha.getTime() > limite.getTime() && n >= 4) break;
  }

  // Comprobar si un slot está cubierto por algún checklist (ventana ±2 meses).
  const TOLERANCIA_MS = 1000 * 60 * 60 * 24 * 31 * 2 + 1000 * 60 * 60 * 24; // ~2 meses
  const cubre = (slot: Slot): boolean => {
    return relevantes.some(c => {
      if (slot.tipo === 'anual') {
        if (c.tipo !== 'anual') return false;
      } else {
        if (c.tipo !== 'trimestral') return false;
        const num =
          slot.tipo === 'trimestral_1' ? 1 :
          slot.tipo === 'trimestral_2' ? 2 : 3;
        if (c.numeroTrimestral !== num) return false;
      }
      return Math.abs(c.fecha.getTime() - slot.fecha.getTime()) <= TOLERANCIA_MS;
    });
  };

  // Con periodo fijado, no se consideran pendientes los huecos anteriores a esa fecha.
  const slotsCandidatos = slotMinimo
    ? slots.filter(s => s.fecha.getTime() >= startOfDay(slotMinimo).getTime())
    : slots;

  // Trimestral finalizada sin numeroTrimestral → requiere aclaración.
  const hayTrimestralSinNumero = relevantes.some(
    c => c.tipo === 'trimestral' && !c.numeroTrimestral,
  );
  if (hayTrimestralSinNumero) {
    const primerNoCubierto = slotsCandidatos.find(s => !cubre(s)) ?? slots[slots.length - 1];
    const proximaAnualSlot = slots.find(s => s.tipo === 'anual' && s.fecha.getTime() >= primerNoCubierto.fecha.getTime());
    return {
      estado: 'requiere_aclaracion',
      ultima: ultimaInfo,
      proxima: { fecha: primerNoCubierto.fecha, tipo: primerNoCubierto.tipo },
      proximaAnual: proximaAnualSlot ? { fecha: proximaAnualSlot.fecha } : undefined,
      diasDesfase: 0,
      categoria,
    };
  }

  const proximoSlot = slotsCandidatos.find(s => !cubre(s)) ?? slots[slots.length - 1];
  const proximaAnualSlot = slots.find(
    s => s.tipo === 'anual' && s.fecha.getTime() >= proximoSlot.fecha.getTime(),
  );

  const diasDesfase = Math.round(
    (hoy0.getTime() - startOfDay(proximoSlot.fecha).getTime()) / 86400000,
  );
  const estado: EstadoCobertura =
    diasDesfase > 0 ? 'vencido' : diasDesfase >= -DIAS_AVISO ? 'proximo' : 'al_dia';

  return {
    estado,
    ultima: ultimaInfo,
    proxima: { fecha: proximoSlot.fecha, tipo: proximoSlot.tipo },
    proximaAnual: proximaAnualSlot ? { fecha: proximaAnualSlot.fecha } : undefined,
    diasDesfase,
    categoria,
  };
}


export interface SugerenciaRevision {
  tipoRevision: 'trimestral' | 'anual';
  numeroTrimestral: 1 | 2 | 3 | null;
  periodo: string; // 'YYYY-MM'
  fechaPropuesta: Date;
  motivo: 'primera' | 'siguiente_ciclo' | 'requiere_aclaracion';
}

function toPeriodo(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Sugiere la próxima revisión a realizar para un sistema, basándose en el historial
 * de checklists. Reutiliza `calcularCobertura` para mantener una única fuente de verdad.
 * Pensado para preseleccionar el formulario del técnico y para abrir OTs automáticas.
 */
export function sugerirProximaRevision(
  sistemaTipo: string,
  checklists: ChecklistInput[],
  hoy: Date = new Date(),
  ancla?: AnclaRevision | null,
): SugerenciaRevision {
  const cobertura = calcularCobertura(sistemaTipo, checklists, false, hoy, ancla);
  const categoria = cobertura.categoria;

  // Sin historial: arrancar siempre por anual.
  if (cobertura.estado === 'sin_iniciar') {
    return {
      tipoRevision: 'anual',
      numeroTrimestral: null,
      periodo: toPeriodo(hoy),
      fechaPropuesta: hoy,
      motivo: 'primera',
    };
  }

  // PCI con trimestrales ambiguas: proponemos trimestral del mes actual sin nº.
  if (cobertura.estado === 'requiere_aclaracion' || !cobertura.proxima) {
    const base = cobertura.proximaAnual?.fecha ?? hoy;
    return {
      tipoRevision: categoria === 'pci' ? 'trimestral' : 'anual',
      numeroTrimestral: null,
      periodo: toPeriodo(base),
      fechaPropuesta: base,
      motivo: 'requiere_aclaracion',
    };
  }

  const tipoProxima = cobertura.proxima.tipo;
  const tipoRevision: 'trimestral' | 'anual' = tipoProxima === 'anual' ? 'anual' : 'trimestral';
  const numeroTrimestral: 1 | 2 | 3 | null =
    tipoProxima === 'trimestral_1' ? 1 :
    tipoProxima === 'trimestral_2' ? 2 :
    tipoProxima === 'trimestral_3' ? 3 : null;

  return {
    tipoRevision,
    numeroTrimestral,
    periodo: toPeriodo(cobertura.proxima.fecha),
    fechaPropuesta: cobertura.proxima.fecha,
    motivo: 'siguiente_ciclo',
  };
}


